import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronDown,
  Users,
  Calendar,
  Workflow,
  Globe,
  ArrowRight,
  X,
  Menu,
} from "lucide-react";
import { navigationData } from "../../../data/navigation";
import Button from "../../ui/Button/Button";
import "./Navbar.css";

const iconMap = {
  Users: <Users size={18} strokeWidth={1.5} />,
  Calendar: <Calendar size={18} strokeWidth={1.5} />,
  Workflow: <Workflow size={18} strokeWidth={1.5} />,
  Globe: <Globe size={18} strokeWidth={1.5} />,
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Calculate scroll progress
      const windowHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const scrolled = (window.scrollY / windowHeight) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    window.location.href = "/";
  };

  const handleSubmenuClick = (path) => {
    const hash = path.split("#")[1];
    if (location.pathname === "/services") {
      const element = document.getElementById(hash);
      if (element) {
        window.scrollTo({ top: element.offsetTop - 90, behavior: "smooth" });
      }
    } else {
      navigate("/services");
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          window.scrollTo({ top: element.offsetTop - 90, behavior: "smooth" });
        }
      }, 400);
    }
    setDropdownOpen(false);
    setMenuOpen(false);
  };

  return (
    <>
      {menuOpen && (
        <div className="navbar__overlay" onClick={() => setMenuOpen(false)} />
      )}

      <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
        {/* Scroll Progress Bar */}
        <div className="navbar__progress-bar">
          <div
            className="navbar__progress-fill"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        <div className="navbar__container">
          <a
            href="/"
            className={`navbar__logo ${menuOpen ? "navbar__logo--hidden" : ""}`}
            onClick={handleLogoClick}
          >
            {navigationData.logo}
          </a>

          {/* Desktop Menu */}
          <div className="navbar__menu">
            {navigationData.links.map((link, index) => {
              if (link.hasDropdown) {
                return (
                  <div
                    key={index}
                    className="navbar__dropdown-wrapper"
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <Link
                      to={link.path}
                      className={`navbar__link navbar__link--dropdown ${isActive(link.path) ? "navbar__link--active" : ""}`}
                    >
                      {link.text}
                      <ChevronDown
                        size={15}
                        className={`navbar__chevron ${dropdownOpen ? "navbar__chevron--open" : ""}`}
                      />
                    </Link>

                    <div
                      className={`navbar__dropdown ${dropdownOpen ? "navbar__dropdown--open" : ""}`}
                    >
                      {link.submenu.map((item, subIndex) => (
                        <button
                          key={subIndex}
                          className="navbar__dropdown-item"
                          onClick={() => handleSubmenuClick(item.path)}
                        >
                          <span className="navbar__dropdown-icon">
                            {iconMap[item.iconName]}
                          </span>
                          <span className="navbar__dropdown-text">
                            {item.text}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={index}
                  to={link.path}
                  className={`navbar__link ${isActive(link.path) ? "navbar__link--active" : ""}`}
                >
                  {link.text}
                </Link>
              );
            })}
          </div>

          <div className="navbar__cta">
            <Button
              text={navigationData.ctaButton.text}
              variant="primary"
              link={navigationData.ctaButton.path}
            />
          </div>

          <button
            className="navbar__toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <X size={24} strokeWidth={1.5} />
            ) : (
              <Menu size={24} strokeWidth={1.5} />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${menuOpen ? "mobile-menu--open" : ""}`}>
        <div className="mobile-menu__inner">
          <div className="mobile-menu__top" />

          <div className="mobile-menu__links">
            {navigationData.links.map((link, index) => {
              if (link.hasDropdown) {
                return (
                  <div key={index} className="mobile-menu__group">
                    <Link
                      to={link.path}
                      className="mobile-menu__link"
                      onClick={() => setMenuOpen(false)}
                    >
                      <span>{link.text}</span>
                      <ArrowRight
                        size={18}
                        strokeWidth={1.5}
                        className="mobile-menu__arrow"
                      />
                    </Link>

                    <div className="mobile-menu__sub">
                      {link.submenu.map((item, subIndex) => (
                        <button
                          key={subIndex}
                          className="mobile-menu__sub-item"
                          onClick={() => handleSubmenuClick(item.path)}
                        >
                          <span className="mobile-menu__sub-icon">
                            {iconMap[item.iconName]}
                          </span>
                          <span>{item.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={index}
                  to={link.path}
                  className={`mobile-menu__link ${isActive(link.path) ? "mobile-menu__link--active" : ""}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <span>{link.text}</span>
                  <ArrowRight
                    size={18}
                    strokeWidth={1.5}
                    className="mobile-menu__arrow"
                  />
                </Link>
              );
            })}
          </div>

          <div className="mobile-menu__bottom">
            <Button
              text={navigationData.ctaButton.text}
              variant="primary"
              link={navigationData.ctaButton.path}
            />
            <p className="mobile-menu__tagline">
              Strategic marketing for Aesthetic Clinics.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
